import { useEffect, useState } from 'react';
import { useDatabase } from './useDatabase';

export interface WorkflowInstanceDetails {
  id: string;
  title: string;
  titleTemplate: string;
  templateId: string;
  lockedVersion: number;
  versionPreference: 'FollowLatest' | 'Pin';
  parameterValues: Record<string, any>;
  stepStates: Record<string, any>;
  createdAt: string;
}

export function useWorkflowInstance(instanceId: string | undefined) {
  const { database, loading: databaseLoading } = useDatabase();
  const [instance, setInstance] = useState<WorkflowInstanceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !instanceId) {
      if (!database) setLoading(false);
      return;
    }

    try {
      const instances = database.workflow_instances();
      const obj = instances[instanceId];

      if (!obj) {
        setError(`Workflow instance with ID ${instanceId} not found`);
        setInstance(null);
      } else {
        setInstance({
          id: instanceId,
          title: obj.title(),
          titleTemplate: obj.title_template(),
          templateId: obj.template_id(),
          lockedVersion: obj.locked_version(),
          versionPreference: obj.version_preference() as 'FollowLatest' | 'Pin',
          parameterValues: obj.parameter_values(),
          stepStates: obj.step_states(),
          createdAt: obj.created_at(),
        });
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching workflow instance:', err);
      setError(`Error fetching workflow instance: ${err}`);
      setInstance(null);
    } finally {
      setLoading(false);
    }
  }, [database, instanceId]);

  return { instance, loading: loading || databaseLoading, error };
}
